import { BACKEND_API, WEB_API, MICRO_API, ELASTIC_HOST_NAME, ELASTIC_INDEX_NAME, FRONTEND_HOST, MICRO_INTERNAL_API } from "@/config/constants";
import { apiClient } from "@/helpers/request";
import { IOverlay, IDownloadAppOverlay } from "@/helpers/Overlays/VideoPlayer";
import { IAdReward } from "@/components/Modal/AdReward/interfaces";
import { getStudentIdLS } from "@/utils/auth";
import { IBannerBelowVideo } from "@/components/VideoPlayer/interfaces";

export const getOverlayPDFData = (
  params: any
): Promise<{
  data: {
    resources: {
      questionId: number;
      resourceType: string;
      url: any;
      ocr: number;
    }[];
    extra: { title: string };
  };
}> => {
  return apiClient(MICRO_API, `/pdf/${params.entityType}`, {
    method: "POST",
    body: JSON.stringify({ filter: params.filter, limit: params.limit }),
  });
};

export const getVideoData = (
  questionId: string
): Promise<{ meta: any; data: any }> => {
  return apiClient(WEB_API, `/api/v2/answer/get-video-data/${questionId}`);
};

export const getBannerBelowVideo = (
  questionId: string
): Promise<{ data: IBannerBelowVideo }> => {
  return apiClient(BACKEND_API, `/v1/answers/get-video-banner/${questionId}`, {
    headers: {
      origin: FRONTEND_HOST,
    }
  });
}

export const getCustomMatches = (
  locale: string,
  ocrText: string,
  packageLanguage: string,
  videoLanguage: string): Promise<{ meta: any; data: any }> => {
  return apiClient(BACKEND_API, "/v2/search/get-custom-matches", {
    headers: {
      origin: FRONTEND_HOST
    },
    method: "POST",
    body: JSON.stringify({
      locale,
      ocrType: 0,
      ocrText,
      elasticHostName: ELASTIC_HOST_NAME,
      elasticIndexName: ELASTIC_INDEX_NAME,
      searchFieldName: "ocr_text",
      videoLanguage,
      packageLanguage,
      requestType: "duplicate_tagging"
    }),
  })
}

export const getAlternateQuestionsByQuestionId = (
  question_id: number
): Promise<{
  meta: any;
  data: {
    alternate_video_language_obj: {
      question_id: string;
      video_language: string;
      video_language_display: string;
      is_ad_blocked: boolean;
      is_selected: boolean;
    }[];
  };
}> => {
  return apiClient(
    BACKEND_API,
    `/v1/answers/get-alternate-video-solution-by-question-id`,
    {
      query: {
        question_id,
      },
      headers: {
        origin: "www.doubtnut.com",
      },
    }
  );
};

export const getTextSolutionForMatches = (
  qids: string[]
): Promise<{ meta: any; data: any }> => {
  return apiClient(BACKEND_API, `/v1/web/text-solutions`, {
    method: "POST",
    body: JSON.stringify({
      qids
    }),
  });
};

export const generateVideoDeeplink = (
  questionId: number,
  resourceType: string
): Promise<{ meta: any; data?: { url: string } }> => {
  return apiClient(BACKEND_API, `/v2/deeplink/generate`, {
    method: "POST",
    body: JSON.stringify({
      campaign: "Web_VideoPage",
      channel: "Web_VideoPage",
      deeplink: `doubtnutapp://video?qid=${questionId}&page=DEEPLINK_WEB&override_page=1&resource_type=${resourceType}`,
    }),
  }) as any;
};

export const generateDrmLicense = (
  questionId: number
): Promise<{ data: { licenseUrl: string } | { code: number; message: string } }> => {
  return apiClient(MICRO_API, "/video-download/streaming-license", {
    query: { questionId },
  });
};

export const getVideoOverlay = (
  questionId: number,
  studentId: string,
  duration?: number
): Promise<{ data: { overlays: IOverlay[] } }> => {
  return apiClient(BACKEND_API, "/v1/video-overlay", {
    query: { questionId, duration, studentId },
  });
};

export const getAdRewardData = (
  studentId: string,
  isViewed: boolean,
  isStarted?: boolean
): Promise<{ data: IAdReward }> => {
  return apiClient(BACKEND_API, "/v2/web-landing/watched-ad-count", {
    method: "POST",
    body: JSON.stringify({ studentId, isViewed, isStarted }),
  });
};

export const getVideoResource = (questionId: string, answerId: string, studentId: string, page: string) => {
  return apiClient(BACKEND_API, `/v1/answers/video-resource`, {
    query: { questionId, answerId, studentId, page },
  });
};

export const updateVV = (params: any): Promise<{ data: { view_id: number } }> => {
  if (params.ans_id === null) {
    params.answer_video = "text";
  }
  delete params.x_sid;
  return apiClient(BACKEND_API, "/v1/answers/update-answer-view-web", {
    method: "POST",
    body: JSON.stringify(params),
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const updatePromoVV = (params: any): Promise<{ data: { view_id: number } }> => {
  return apiClient(BACKEND_API, "/v1/answers/update-promo-view-web", {
    method: "POST",
    body: JSON.stringify(params),
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getRatings = (questionId: number) => {
  return apiClient(BACKEND_API, `/v4/feedback/rating`, {
    query: { questionId },
  });
}

export const setRatings = (questionId: string, ratings: number[]) => {
  return apiClient(BACKEND_API, `/v4/feedback/rating`, {
    method: "POST",
    body: JSON.stringify({
      questionId,
      ratings
    })
  });
}

export const submitVideoFeedback = (questionId: string, feedback: string[]) => {
  return apiClient(BACKEND_API, `/v4/feedback/submit`, {
    method: "POST",
    body: JSON.stringify({
      questionId,
      webStudentId: getStudentIdLS(),
      feedback,
    })
  });
}

export const setBookmarkVideo = (studentId: string, questionId: number) => {
  return apiClient(BACKEND_API, "/v2/playlist/playlistWrapper", {
    method: "POST",
    body: JSON.stringify({
      "playlist_id": [
        "1"
      ],
      "student_id": studentId,
      "question_id": questionId.toString()
    })
  });
}

export const getBookmarkVideo = () => {
  return apiClient(BACKEND_API, `/v8/library/getresource`, {
    headers: {
      version_code: 958
    },
    query: {
      page_no: 1,
      id: 1,
      auto_play_data: 0,
    },
  })
}

export const getLLMTextSolution = (questionId: string): Promise<{ solution: string; hints: string[]; createdAt: string }> => {
  return apiClient(MICRO_INTERNAL_API, "/api/$text-solution/find", {
    method: "POST",
    body: JSON.stringify({
      query: {
        questionId: +questionId,
        isDeleted: false
      },
      limit: 1,
      fields: ["solution", "createdAt"],
    }),
  }).then(({ data }: any) => data[0] || {});
};

export const getDownloadAppOverlay = (
  questionId: number,
  studentId: number
): Promise<{ data: IDownloadAppOverlay }> => {
  return apiClient(BACKEND_API, "/v1/video-overlay/download-app", {
    query: { questionId, studentId },
  });
};
